import axios from 'axios';
import { getApiUrl } from '../../hooks/commanFunc'; // Single source of truth for legacy base server paths

// Create isolated client instance so it never overrides or pollutes legacy network pipelines
export const cmsApiClient = axios.create({
  baseURL: `${getApiUrl()}/admin/setting/web`, // Resolves cleanly to http://mapi.dev.miira.ca/v1/admin/setting/web
  headers: {
    'Accept': 'application/json',
  },
  withCredentials: true, // CRITICAL: Send HttpOnly cookies with requests
});

// Create isolated client instance so it never overrides or pollutes legacy network pipelines
export const cmsReApiClient = axios.create({
  baseURL: `${getApiUrl()}`, // Resolves cleanly to http://mapi.dev.miira.ca/v1/
  headers: {
    'Accept': 'application/json',
  },
  withCredentials: true, // CRITICAL: Send HttpOnly cookies with requests
  maxRedirects: 0, // Prevent axios from following redirects automatically
  validateStatus: function (status) {
    // Accept both success responses and redirects
    return status >= 200 && status < 400;
  },
});

// Store token getter function - set by TokenBridge/TokenContext after initialization
let getAccessTokenFn = null;

/**
 * Register token getter function from TokenContext
 * This allows axios interceptors to access in-memory tokens
 */
export const registerTokenGetter = (getTokenFn) => {
  getAccessTokenFn = getTokenFn;
};

/**
 * Helper to get active access token from memory, window bridge, or localStorage fallback
 */
export const getActiveAccessToken = () => {
  if (getAccessTokenFn) {
    const token = getAccessTokenFn();
    if (token) return token;
  }
  if (typeof window !== 'undefined' && window.__getAccessToken) {
    const token = window.__getAccessToken();
    if (token) return token;
  }
  const userData = localStorage.getItem('user');
  if (userData) {
    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser?.jwt) return parsedUser.jwt;
    } catch (e) {}
  }
  return null;
};

// Track if we're currently refreshing to prevent multiple simultaneous refresh requests
let isRefreshing = false;
let refreshSubscribers = [];

/**
 * Subscribe to token refresh completion
 * Multiple failed requests will queue here and retry after refresh
 */
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

/**
 * Notify all subscribers that token refresh completed
 */
const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
};

/**
 * Attach authentication request and response interceptors to any Axios instance
 */
export const attachAuthInterceptors = (instance) => {
  // Request Interceptor: Attach Bearer token from memory
  instance.interceptors.request.use(
    (config) => {
      const token = getActiveAccessToken();
      if (token && !config.headers['Authorization']) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response Interceptor: Handle redirects, 401s, and automatic silent refresh
  instance.interceptors.response.use(
    (response) => {
      // Handle 302/301 redirects (OAuth flow)
      if (response.status === 302 || response.status === 301) {
        const redirectUrl = response.headers?.location || response.headers?.Location;
        if (redirectUrl) {
          return {
            ...response,
            data: {
              code: 0,
              data: {
                redirectUrl: redirectUrl,
                status: response.status
              }
            }
          };
        }
      }

      // Check if response indicates a redirect
      if (response.data?.redirect || response.data?.redirectUrl || response.data?.authorizationUrl) {
        return response;
      }

      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      if (!originalRequest) return Promise.reject(error);

      // Handle redirects that come through as errors
      if (error.response?.status === 302 || error.response?.status === 301) {
        const redirectUrl = error.response.headers?.location || error.response.headers?.Location;
        if (redirectUrl) {
          return Promise.resolve({
            status: error.response.status,
            data: {
              code: 0,
              data: {
                redirectUrl: redirectUrl,
                status: error.response.status
              }
            }
          });
        }
      }

      // Avoid infinite loop on auth endpoints
      const isAuthEndpoint = originalRequest.url?.includes('/login') ||
                             originalRequest.url?.includes('/verify-otp') ||
                             originalRequest.url?.includes('/refresh-token');

      // Handle 401 Unauthorized - attempt silent token refresh
      if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve) => {
            subscribeTokenRefresh((newToken) => {
              if (newToken) {
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                resolve(instance(originalRequest));
              } else {
                window.location.href = '/login';
                resolve(Promise.reject(error));
              }
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          console.log('🔄 Access token expired, attempting refresh...');

          // Call refresh endpoint (HttpOnly cookie sent automatically; payload fallback for localhost)
          const refreshPayload = {};
          const rtFallback = (typeof window !== 'undefined' && window.__refreshTokenFallback) ||
                             (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('rt_fallback'));
          if (rtFallback) {
            refreshPayload.refreshToken = rtFallback;
          }

          const refreshResponse = await axios.post(
            `${getApiUrl()}/refresh-token`,
            refreshPayload,
            { withCredentials: true }
          );

          if (refreshResponse.data?.refreshToken) {
            if (typeof window !== 'undefined') {
              window.__refreshTokenFallback = refreshResponse.data.refreshToken;
              try { sessionStorage.setItem('rt_fallback', refreshResponse.data.refreshToken); } catch (e) {}
            }
          }

          const newAccessToken = refreshResponse.data?.token || refreshResponse.data?.jwt || refreshResponse.data?.data?.jwt;

          if (newAccessToken) {
            // Update token in memory via TokenContext
            if (window.__setAccessToken) {
              window.__setAccessToken(newAccessToken);
            }

            console.log('✅ Token refreshed successfully');

            // Update authorization header for original request
            originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;

            // Notify all queued requests
            onTokenRefreshed(newAccessToken);
            isRefreshing = false;

            // Retry original request with the current instance
            return instance(originalRequest);
          } else {
            throw new Error('Token refresh response missing access token');
          }
        } catch (refreshError) {
          console.error('❌ Token refresh failed:', refreshError);

          // Notify queued requests that refresh failed
          onTokenRefreshed(null);
          isRefreshing = false;

          // Clear stored user data and redirect to login
          localStorage.removeItem('user');
          if (window.__clearAccessToken) {
            window.__clearAccessToken();
          }

          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// Initialize interceptors for isolated clients
attachAuthInterceptors(cmsApiClient);
attachAuthInterceptors(cmsReApiClient);
